import { Loader2 } from 'lucide-react';
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert';
import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { cn } from '@/lib/utils';

interface TransitionModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  /** Title shown at the top of the dialog. */
  title: string;
  /** Free-form description rendered above the inputs. */
  description: string;
  confirmLabel: string;
  /** Visual variant of the confirm button. Use 'destructive' for
   *  cancel/reject, 'default' for forward progress (start, complete). */
  variant?: 'default' | 'destructive';
  /** When true, the modal renders a textarea for `reason` and forces
   *  the user to fill it before submitting. */
  requireReason?: boolean;
  /** When true, the modal renders an optional `actualHours` input
   *  (decimal, 0..9999.99). */
  showActualHours?: boolean;
  loading: boolean;
  /** Translated error message to display in a banner inside the dialog. */
  errorMessage?: string | null;
  /** Called with the collected fields. The mutation invocation lives
   *  in the caller so the modal stays presentation-only. */
  onConfirm: (input: { reason?: string; actualHours?: number }) => void;
}

/**
 * TransitionModal — generic confirmation dialog for Work Order state
 * machine transitions. Two optional inputs:
 *   - `reason`: textarea, required when transitioning to `cancelled`
 *     (matches backend STATUSES_REQUIRING_CANCEL_REASON).
 *   - `actualHours`: numeric, used when completing the WO
 *     (transitioning to pending_review).
 *
 * The modal is intentionally presentation-only: it doesn't know
 * about the WO id, the new status, or the mutation hook. Callers
 * pass `onConfirm` and decide what payload to send. This keeps the
 * primitive reusable for any other transition that grows similar
 * inputs in the future (Phase 3c sign, F5.4b).
 */
export function TransitionModal({
  open,
  onOpenChange,
  title,
  description,
  confirmLabel,
  variant = 'default',
  requireReason = false,
  showActualHours = false,
  loading,
  errorMessage,
  onConfirm,
}: TransitionModalProps) {
  const { t } = useTranslation();
  const [reason, setReason] = useState('');
  const [actualHours, setActualHours] = useState('');

  // Reset internal state every time the dialog opens.
  useEffect(() => {
    if (open) {
      setReason('');
      setActualHours('');
    }
  }, [open]);

  const reasonInvalid = requireReason && reason.trim().length === 0;
  const actualHoursValue = (() => {
    if (!showActualHours || actualHours.trim() === '') return undefined;
    const n = Number(actualHours);
    return Number.isFinite(n) && n >= 0 && n <= 9999.99 ? n : NaN;
  })();
  const actualHoursInvalid =
    showActualHours && actualHours.trim() !== '' && Number.isNaN(actualHoursValue);

  const canSubmit = !loading && !reasonInvalid && !actualHoursInvalid;

  const submit = () => {
    if (!canSubmit) return;
    onConfirm({
      reason: requireReason ? reason.trim() : undefined,
      actualHours:
        showActualHours && actualHoursValue !== undefined && !Number.isNaN(actualHoursValue)
          ? actualHoursValue
          : undefined,
    });
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>{title}</DialogTitle>
          <DialogDescription>{description}</DialogDescription>
        </DialogHeader>

        {errorMessage && (
          <Alert variant="destructive">
            <AlertTitle>{t('common.error', { defaultValue: 'Error' })}</AlertTitle>
            <AlertDescription>{errorMessage}</AlertDescription>
          </Alert>
        )}

        <div className="space-y-3">
          {requireReason && (
            <div className="space-y-1">
              <label className="text-sm font-medium" htmlFor="transition-reason">
                {t('workOrder.transition.reasonLabel')}
              </label>
              <textarea
                id="transition-reason"
                rows={3}
                value={reason}
                onChange={(e) => setReason(e.target.value)}
                className="flex min-h-[80px] w-full rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2"
                aria-invalid={reasonInvalid}
              />
              {reasonInvalid && (
                <p className="text-xs text-destructive">
                  {t('workOrder.transition.reasonRequired')}
                </p>
              )}
            </div>
          )}

          {showActualHours && (
            <div className="space-y-1">
              <label className="text-sm font-medium" htmlFor="transition-hours">
                {t('workOrder.transition.actualHoursLabel')}
              </label>
              <Input
                id="transition-hours"
                type="number"
                step="0.01"
                min="0"
                max="9999.99"
                value={actualHours}
                onChange={(e) => setActualHours(e.target.value)}
                aria-invalid={actualHoursInvalid}
              />
              {actualHoursInvalid && (
                <p className="text-xs text-destructive">
                  {t('workOrder.transition.actualHoursInvalid')}
                </p>
              )}
            </div>
          )}
        </div>

        <DialogFooter>
          <Button
            variant="ghost"
            onClick={() => onOpenChange(false)}
            disabled={loading}
          >
            {t('common.cancel')}
          </Button>
          <Button
            variant={variant}
            onClick={submit}
            disabled={!canSubmit}
            className={cn(loading && 'opacity-70')}
          >
            {loading && <Loader2 className="animate-spin" aria-hidden />}
            {confirmLabel}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
